import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
  conciseId,
} from '../../../../components/admin/AdminManagement';
import { Alert } from '../../../../components/Alert';
import { BackLink } from '../../../../components/BackLink';
import { PageSpinner } from '../../../../components/PageSpinner';
import { useAuth } from '../../../../features/auth/auth-context';
import { billingApi } from '../../../../features/billing/billing.api';
import { operationalApi } from '../../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatDateTime } from '../../../../lib/format-date';
import { formatStatusLabel } from '../../../../lib/format-status';
import { paths, routeTo } from '../../../../routes/paths';

export function AdminPaymentsPage(): React.JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const { can } = useAuth();
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const payments = useQuery({
    queryKey: ['admin', 'payments', subscriptionFilter.trim()],
    queryFn: () =>
      billingApi.listAdminPayments({
        pageSize: 50,
        subscriptionId: subscriptionFilter.trim() || undefined,
      }),
    enabled: id === undefined,
  });
  const detail = useQuery({
    queryKey: ['admin', 'payments', id],
    queryFn: () => billingApi.getAdminPayment(id ?? ''),
    enabled: id !== undefined,
  });
  const summary = useQuery({
    queryKey: ['admin', 'payments', 'summary'],
    queryFn: () => operationalApi.paymentSummary(),
    enabled: id === undefined,
  });
  const canReadAttempts = can(PERMISSIONS.PAYMENT_ATTEMPTS_READ);
  const failures = useQuery({
    queryKey: ['admin', 'payments', 'failure-queue'],
    queryFn: () => operationalApi.paymentFailureQueue(1, 5),
    enabled: id === undefined && canReadAttempts,
  });
  const canViewReceipt = can(PERMISSIONS.RECEIPTS_READ);
  if (id !== undefined) {
    if (detail.isPending) return <PageSpinner label="Loading payment" />;
    if (detail.isError) return <Alert tone="error">{toDisplayMessage(detail.error)}</Alert>;
    const payment = detail.data;
    return (
      <div className={styles.page}>
        <BackLink to={paths.adminPayments}>Back to posted payments</BackLink>
        <ManagementHeader
          eyebrow="Posted transaction"
          title="Posted payment"
          subtitle={`${payment.internalReference} · Authoritative settled payment record.`}
          icon="payment"
          context="Payment detail"
        />
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="payment" />
              </span>
              <div>
                <h2>Payment details</h2>
                <p>Transaction and schedule linkage</p>
              </div>
            </div>
            {canViewReceipt && (
              <Link className={styles.buttonLink} to={routeTo.adminPaymentReceipt(payment.id)}>
                View receipt
              </Link>
            )}
          </header>
          <div className={styles.summaryBody}>
            <dl className={styles.summaryGrid}>
              <div>
                <dt>Amount</dt>
                <dd>{formatNgn(payment.amount)}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{formatStatusLabel(payment.status)}</dd>
              </div>
              <div>
                <dt>Contract</dt>
                <dd>{conciseId(payment.subscriptionId)}</dd>
              </div>
              <div>
                <dt>Method</dt>
                <dd>{payment.paymentMethod}</dd>
              </div>
              <div>
                <dt>Provider transaction</dt>
                <dd>{payment.providerTransactionId ?? '—'}</dd>
              </div>
              <div>
                <dt>Posted</dt>
                <dd>{formatDateTime(payment.postedAt)}</dd>
              </div>
            </dl>
          </div>
        </section>
      </div>
    );
  }
  const items = payments.data?.items ?? [];
  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Payment operations"
        title="Payments"
        subtitle="Review posted transactions while keeping payment attempts distinct."
        icon="payment"
        context="Posted transactions"
      />
      <section className={styles.hero}>
        <div>
          <h2>Track confirmed money movement</h2>
          <p>
            Posted payments are settled transactions; failed and processing records remain attempts.
          </p>
        </div>
        <strong>{summary.data ? formatNgn(summary.data.postedAmount) : 'Payments'}</strong>
      </section>
      <section className={styles.metrics}>
        <MetricCard label="Posted payments" value={summary.data?.postedCount ?? '—'} icon="check" />
        <MetricCard
          label="Posted amount"
          value={summary.data ? formatNgn(summary.data.postedAmount) : '—'}
          icon="money"
          tone="blue"
        />
        <MetricCard
          label="Processing attempts"
          value={summary.data?.processingAttemptCount ?? '—'}
          icon="clock"
          tone="amber"
        />
        <MetricCard
          label="Failed attempts"
          value={summary.data?.failedAttemptCount ?? '—'}
          icon="warning"
          tone="violet"
        />
      </section>
      <form className={styles.filterPanel}>
        <label>
          Contract ID
          <input
            value={subscriptionFilter}
            placeholder="Filter by subscription UUID"
            onChange={(event) => setSubscriptionFilter(event.target.value)}
          />
        </label>
      </form>
      <div className={styles.contentGrid}>
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="payment" />
              </span>
              <div>
                <h2>Posted payment ledger</h2>
                <p>Confirmed payment transactions only</p>
              </div>
            </div>
            {payments.data && (
              <span className={styles.countBadge}>
                {payments.data.pagination.totalItems} posted
              </span>
            )}
          </header>
          {payments.isPending && <p className={styles.empty}>Loading payments…</p>}
          {payments.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">{toDisplayMessage(payments.error)}</Alert>
            </div>
          )}
          {payments.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No posted payments match this contract.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Contract</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Posted</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((payment) => (
                    <tr key={payment.id}>
                      <td>
                        <strong>{payment.internalReference}</strong>
                      </td>
                      <td>{conciseId(payment.subscriptionId)}</td>
                      <td>{formatNgn(payment.amount)}</td>
                      <td>{payment.paymentMethod}</td>
                      <td>{formatDateTime(payment.postedAt)}</td>
                      <td>
                        <span className={`${styles.status} ${styles.success}`}>
                          {formatStatusLabel(payment.status)}
                        </span>
                      </td>
                      <td>
                        <Link to={routeTo.adminPayment(payment.id)}>Details →</Link>
                        {canViewReceipt && (
                          <>
                            <span> · </span>
                            <Link to={routeTo.adminPaymentReceipt(payment.id)}>Receipt</Link>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className={styles.rail}>
          {canReadAttempts && (
            <section className={styles.railCard}>
              <h2>Failed attempt queue</h2>
              <p>Attempts requiring attention; these are not posted transactions.</p>
              <ul className={styles.railList}>
                {failures.data?.items.map((attempt) => (
                  <li key={attempt.attemptId}>
                    <span>
                      <i className={styles.dot} />
                      {formatNgn(attempt.amount)} · {attempt.failureCode ?? 'Failed'}
                    </span>
                    <Link to={attempt.detailRoute}>Review</Link>
                  </li>
                ))}
                {failures.data?.items.length === 0 && <li>No failed attempts</li>}
              </ul>
            </section>
          )}
          <section className={styles.railCard}>
            <h2>Payment safety</h2>
            <p>
              OTP values, authorization tokens, provider secrets and raw provider payloads are not
              exposed on this screen.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
